import React, { FC } from 'react';
import TwInitialsIndicator from '../TwInitialsIndicator/TwInitialsIndicator';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TwGenericMenu from '../TwGenericMenu/TwGenericMenu';
import TwTooltip from '../TwTooltip/TwTooltip';
import { DispatchNoteResponse, DispatchNoteType } from './TwDispatchNotes';
import DateUtils from '../../utils/dateUtils';
import { ReactComponent as DispatchOnly } from '../../assets/icons/dispatch-only-lock.svg';
import { ReactComponent as DispatchAndDrivers } from '../../assets/icons/driver-wheel.svg';
import useAuth from '../../providers/Auth/Auth.provider';
import useFormatUrlsInText from '../../hooks/useFormatUrlText';
import { USER_ROLE } from '../../providers/User/User.model';

interface TwDispatchNotesMessagesProps {
  note: DispatchNoteResponse;
  orderReadableId: string | number;
  driverId?: string;
  isDriverLink?: boolean;
  handleDeleteNote: (note: DispatchNoteResponse) => void;
  ltlEnabled: boolean;
}

const TwDispatchNotesMessages: FC<TwDispatchNotesMessagesProps> = ({
  note,
  orderReadableId,
  handleDeleteNote,
  isDriverLink,
  driverId,
  ltlEnabled
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const formatUrlsInText = useFormatUrlsInText();
  const isDriverOrCarrier =
    userData?.role === USER_ROLE.DRIVER || userData?.role === USER_ROLE.CARRIER;

  const deleteNote = (): void => {
    handleDeleteNote(note);
  };

  const formatTripNumber = (): string => {
    if (ltlEnabled) {
      return note.tripReadableId ? ` • Trip #${note.tripReadableId}` : '';
    } else {
      return note.tripSortOrder !== undefined && note.hasOwnProperty('tripSortOrder')
        ? ` • Trip #${orderReadableId}-${note.tripSortOrder + 1}`
        : '';
    }
  };

  return (
    <>
      <Stack flexDirection={'row'} className="dispatch-notes__message-container">
        <TwInitialsIndicator
          displayRawText
          text={note.user.initials}
          className="dispatch-notes__initials"
        />
        <Stack className="dispatch-notes__message-container__texts">
          <Typography fontWeight={'500'} fontSize={'14px'}>
            {`${note.user.first_name} ${note.user.last_name}`}
          </Typography>

          <div className="dispatch-notes__message-container__note">
            {formatUrlsInText(note.dispatch_note.note || '')}
          </div>
          <Box className="dispatch-notes__message-container__texts__dates">
            <Box>
              <span>{`${DateUtils.getMediumDateInLocalTimeZone(
                new Date(note.dispatch_note.created_at)
              )} ${DateUtils.get12HourFormattedTime(
                new Date(note.dispatch_note.created_at)
              )}`}</span>
              <span>{formatTripNumber()}</span>
            </Box>
          </Box>
        </Stack>
        <Stack className="dispatch-notes__message-container__options">
          <TwTooltip
            title={
              note.dispatch_note.note_type === DispatchNoteType.DISPATCH_ONLY
                ? 'Dispatch only'
                : 'Dispatch and drivers'
            }
            placement="top"
          >
            <Box className="dispatch-notes__message-container__type">
              {note.dispatch_note.note_type === DispatchNoteType.DISPATCH_ONLY ? (
                <DispatchOnly />
              ) : (
                <DispatchAndDrivers />
              )}
            </Box>
          </TwTooltip>
          {note.dispatch_note.created_by === userData?.id ||
          (driverId &&
            note.user.role === USER_ROLE.DRIVER &&
            note.user.id === driverId &&
            isDriverLink) ||
          (!isDriverOrCarrier &&
            note.dispatch_note.created_by !== userData?.id &&
            !isDriverLink) ? (
            <TwGenericMenu
              className="dispatch-notes__message-container__menu"
              options={[
                {
                  label: 'Delete',
                  action: deleteNote,
                  className: 'detail-file__delete'
                }
              ]}
            />
          ) : (
            ''
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default TwDispatchNotesMessages;
